// Batches and expiry: deterministic batch ids, first-expiry-first-out
// allocation, and expiry status. Pure (no I/O, no platform APIs).
import { CoreError, assertSafeInt } from './errors'
import type { Milli } from './qty'

// -----------------------------------------------------------------------------
// UUIDv5 (RFC 4122 name-based, SHA-1)
// -----------------------------------------------------------------------------

function utf8(text: string): number[] {
  const out: number[] = []
  for (const ch of text) {
    let c = ch.codePointAt(0)!
    if (c < 0x80) out.push(c)
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f))
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f))
    else {
      out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f))
      c &= 0x3f
      out.push(0x80 | c)
    }
  }
  return out
}

function sha1(bytes: number[]): number[] {
  const ml = bytes.length * 8
  const msg = [...bytes, 0x80]
  while (msg.length % 64 !== 56) msg.push(0)
  // Message length in bits as a 64-bit big-endian integer (inputs here are small).
  const hi = Math.floor(ml / 0x100000000)
  const lo = ml >>> 0
  msg.push((hi >>> 24) & 255, (hi >>> 16) & 255, (hi >>> 8) & 255, hi & 255)
  msg.push((lo >>> 24) & 255, (lo >>> 16) & 255, (lo >>> 8) & 255, lo & 255)

  let h0 = 0x67452301
  let h1 = 0xefcdab89
  let h2 = 0x98badcfe
  let h3 = 0x10325476
  let h4 = 0xc3d2e1f0
  const w = new Array<number>(80)
  const rotl = (x: number, n: number) => (x << n) | (x >>> (32 - n))
  for (let i = 0; i < msg.length; i += 64) {
    for (let t = 0; t < 16; t++) {
      w[t] =
        (msg[i + 4 * t]! << 24) |
        (msg[i + 4 * t + 1]! << 16) |
        (msg[i + 4 * t + 2]! << 8) |
        msg[i + 4 * t + 3]!
    }
    for (let t = 16; t < 80; t++) w[t] = rotl(w[t - 3]! ^ w[t - 8]! ^ w[t - 14]! ^ w[t - 16]!, 1)
    let a = h0
    let b = h1
    let c = h2
    let d = h3
    let e = h4
    for (let t = 0; t < 80; t++) {
      const [f, k] =
        t < 20
          ? [(b & c) | (~b & d), 0x5a827999]
          : t < 40
            ? [b ^ c ^ d, 0x6ed9eba1]
            : t < 60
              ? [(b & c) | (b & d) | (c & d), 0x8f1bbcdc]
              : [b ^ c ^ d, 0xca62c1d6]
      const temp = (rotl(a, 5) + f + e + k + w[t]!) | 0
      e = d
      d = c
      c = rotl(b, 30)
      b = a
      a = temp
    }
    h0 = (h0 + a) | 0
    h1 = (h1 + b) | 0
    h2 = (h2 + c) | 0
    h3 = (h3 + d) | 0
    h4 = (h4 + e) | 0
  }
  const out: number[] = []
  for (const h of [h0, h1, h2, h3, h4]) {
    out.push((h >>> 24) & 255, (h >>> 16) & 255, (h >>> 8) & 255, h & 255)
  }
  return out
}

/** Name-based UUID (version 5) of `name` in `namespace`. */
export function uuidV5(namespace: string, name: string): string {
  const hex = namespace.replace(/-/g, '')
  if (!/^[0-9a-f]{32}$/i.test(hex)) throw new CoreError('invalid_input', 'namespace must be a UUID')
  const ns = hex.match(/../g)!.map((b) => parseInt(b, 16))
  const hash = sha1([...ns, ...utf8(name)]).slice(0, 16)
  hash[6] = (hash[6]! & 0x0f) | 0x50
  hash[8] = (hash[8]! & 0x3f) | 0x80
  const h = hash.map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/** Namespace for batch ids (fixed; changing it would split existing batches). */
export const BATCH_NAMESPACE = '6f7a1f4e-2b1c-5d8e-9a3b-0c4d5e6f7a8b'

/**
 * The id of a product's batch. The same batch number received on two devices
 * (even offline) gets the same id, so both upserts land on one row.
 */
export function batchId(tenantId: string, productId: string, batchNo: string): string {
  const no = batchNo.trim().toLowerCase()
  if (!no) throw new CoreError('invalid_input', 'a batch number is required')
  return uuidV5(BATCH_NAMESPACE, `${tenantId}:${productId}:${no}`)
}

// -----------------------------------------------------------------------------
// Dates
// -----------------------------------------------------------------------------

function toUtc(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  if (!m) throw new CoreError('invalid_input', `“${date}” is not a date`)
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/** Whole days from `today` to `date` (negative when it's in the past). */
export function daysUntil(date: string, today: string): number {
  return Math.round((toUtc(date) - toUtc(today)) / 86_400_000)
}

/** What a product's shelf life counts (`products.shelf_life_unit`; null is days). */
export type ShelfLifeUnit = 'day' | 'month' | 'year'

/**
 * Expiry from a manufacturing date and a shelf life. Days count the mfg day as day 1. Months
 * and years are on the calendar, as packs print them: the day before the same date that many
 * months on, or the last day of that month when the date does not exist (31 Jan + 1 month is
 * 28 Feb).
 */
export function expiryFromShelfLife(
  mfgDate: string,
  amount: number,
  unit: ShelfLifeUnit | null = 'day',
): string {
  assertSafeInt(amount, 'shelfLife')
  if (amount <= 0) throw new CoreError('invalid_input', 'shelf life must be positive')
  if (unit === null || unit === 'day') {
    // Expires at the end of the last day of shelf life.
    return fromUtc(toUtc(mfgDate) + (amount - 1) * 86_400_000)
  }
  const months = unit === 'year' ? amount * 12 : amount
  const [y, m, d] = mfgDate.slice(0, 10).split('-').map(Number) as [number, number, number]
  const first = Date.UTC(y, m - 1 + months, 1)
  const target = new Date(first)
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate()
  if (d > lastDay) return fromUtc(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), lastDay))
  return fromUtc(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), d) - 86_400_000)
}

/** "24 months", "1 year", "730 days". */
export function shelfLifeLabel(amount: number, unit: ShelfLifeUnit | null): string {
  const word = unit ?? 'day'
  return `${amount} ${word}${amount === 1 ? '' : 's'}`
}

/**
 * A date a person has finished typing: a date box reports "0002-09-15" while the year is
 * still being typed, so a year outside 1990–2100 is not a date yet.
 */
export function isWholeDate(value: string | null | undefined): value is string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '')
  if (!m) return false
  const year = Number(m[1])
  return year >= 1990 && year <= 2100
}

export type ExpiryStatus = 'expired' | 'soon' | 'ok' | 'none'

/** A batch is expired from the day after its expiry date; "soon" within `alertDays`. */
export function expiryStatus(
  expiryDate: string | null | undefined,
  today: string,
  alertDays: number,
): ExpiryStatus {
  if (!expiryDate) return 'none'
  const days = daysUntil(expiryDate, today)
  if (days < 0) return 'expired'
  if (days <= alertDays) return 'soon'
  return 'ok'
}

// -----------------------------------------------------------------------------
// First expiry, first out
// -----------------------------------------------------------------------------

export interface BatchStock {
  batchId: string
  qtyMilli: Milli
  expiryDate: string | null
}

export interface FefoResult {
  allocations: { batchId: string; qtyMilli: Milli }[]
  /** Quantity no (usable) batch covers. */
  remainderMilli: Milli
  /** True when expired stock was skipped and would have covered some of the remainder. */
  expiredSkipped: boolean
}

/**
 * Splits a sale across batches, earliest expiry first (batches without an
 * expiry last). Expired batches are skipped unless `includeExpired`.
 */
export function allocateFefo(
  batches: readonly BatchStock[],
  qtyMilli: Milli,
  today: string,
  options: { includeExpired?: boolean } = {},
): FefoResult {
  assertSafeInt(qtyMilli, 'qtyMilli')
  const usable = batches
    .filter((b) => b.qtyMilli > 0)
    .sort(
      (a, b) =>
        (a.expiryDate ?? '9999-12-31').localeCompare(b.expiryDate ?? '9999-12-31') ||
        a.batchId.localeCompare(b.batchId),
    )
  const allocations: FefoResult['allocations'] = []
  let left = qtyMilli
  let expiredQty = 0
  for (const b of usable) {
    const expired = b.expiryDate !== null && daysUntil(b.expiryDate, today) < 0
    if (expired && !options.includeExpired) {
      expiredQty += b.qtyMilli
      continue
    }
    if (left <= 0) break
    const take = Math.min(left, b.qtyMilli)
    allocations.push({ batchId: b.batchId, qtyMilli: take })
    left -= take
  }
  return { allocations, remainderMilli: left, expiredSkipped: left > 0 && expiredQty > 0 }
}
